import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Home, History, User, LogOut, Sprout, Smartphone } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n/LanguageContext";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { HansaLogo, PoweredByBadge } from "./HansaLogo";

export const BottomNav = () => {
  const loc = useLocation();
  const { t } = useLang();
  const items = [
    { to: "/dashboard", icon: Home, label: t("nav.home"), testid: "nav-home" },
    { to: "/fields", icon: Sprout, label: t("nav.fields"), testid: "nav-fields" },
    { to: "/history", icon: History, label: t("nav.history"), testid: "nav-history" },
    { to: "/download", icon: Smartphone, label: t("nav.download"), testid: "nav-download" },
    { to: "/profile", icon: User, label: t("nav.profile"), testid: "nav-profile" },
  ];
  return (
    <nav
      data-testid="bottom-nav"
      className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-xl border-t border-[#E5E0D8] flex justify-around py-2 z-40 md:hidden"
    >
      {items.map(({ to, icon: Icon, label, testid }) => {
        const active = loc.pathname.startsWith(to);
        return (
          <Link
            to={to}
            key={to}
            data-testid={testid}
            className="flex flex-col items-center gap-1 px-3 py-2 text-[11px]"
          >
            <Icon
              className={`w-6 h-6 transition-transform duration-200 ${
                active ? "text-[#1B4332] scale-110" : "text-[#52796F]"
              }`}
              strokeWidth={active ? 2.4 : 1.8}
            />
            <span className={active ? "text-[#1B4332] font-semibold" : "text-[#52796F]"}>
              {label}
            </span>
          </Link>
        );
      })}
    </nav>
  );
};

export const TopBar = ({ title }) => {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  return (
    <header
      data-testid="top-bar"
      className="sticky top-0 z-30 bg-[#FDFBF7]/85 backdrop-blur-md border-b border-[#E5E0D8]"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
        <Link to="/dashboard" data-testid="logo-link" className="shrink-0">
          <HansaLogo size={40} />
        </Link>
        <div className="hidden md:flex items-center gap-1">
          <Link to="/dashboard" data-testid="top-nav-home" className="px-3 py-2 text-sm font-semibold text-[#2D3A35] hover:text-[#1B4332]">{t("nav.home")}</Link>
          <Link to="/fields" data-testid="top-nav-fields" className="px-3 py-2 text-sm font-semibold text-[#2D3A35] hover:text-[#1B4332]">{t("nav.fields")}</Link>
          <Link to="/history" data-testid="top-nav-history" className="px-3 py-2 text-sm font-semibold text-[#2D3A35] hover:text-[#1B4332]">{t("nav.history")}</Link>
          <Link
            to="/download"
            data-testid="top-nav-download"
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-[#1B4332] hover:text-[#2D6A4F]"
          >
            <Smartphone className="w-4 h-4" /> {t("nav.download")}
          </Link>
          {user?.is_admin && (
            <Link
              to="/admin"
              data-testid="top-nav-admin"
              className="px-3 py-2 text-sm font-bold text-[#1B4332] bg-[#D4A373]/30 rounded-full ml-1 hover:bg-[#D4A373]/50"
            >
              Admin
            </Link>
          )}
        </div>
        <div className="flex items-center gap-3">
          <LanguageSwitcher compact />
          {title && (
            <span className="hidden sm:inline text-sm text-[#52796F]">{title}</span>
          )}
          {user && (
            <button
              data-testid="logout-btn"
              onClick={() => {
                logout();
                nav("/");
              }}
              className="flex items-center gap-1 text-sm text-[#2D3A35] hover:text-[#D90429] transition-colors"
              aria-label="Logout"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">{t("common.logout")}</span>
            </button>
          )}
        </div>
      </div>
    </header>
  );
};
